import React, { useState, useEffect, useCallback } from 'react';
import {
  UserCog, Plus, Search, X, Edit2, Trash2,
  Mail, Phone, Shield, User, Crown, Eye, EyeOff,
  CheckCircle2, XCircle
} from 'lucide-react';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE_URL;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('mideast_token')}` }
});

const STYLES = `
  .mu-header {
    display: flex; align-items: center;
    justify-content: space-between;
    margin-bottom: 24px; gap: 16px;
  }
  .mu-toolbar {
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 20px; flex-wrap: wrap;
  }
  .mu-search-wrap {
    position: relative; flex: 1; max-width: 320px;
  }
  .mu-search-icon {
    position: absolute; left: 12px; top: 50%;
    transform: translateY(-50%);
    color: rgba(255,255,255,0.25); pointer-events: none;
  }
  .mu-search {
    width: 100%; padding: 9px 14px 9px 38px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px; color: #E2E8F0;
    font-family: 'DM Sans', sans-serif; font-size: 13px;
    outline: none; transition: border-color 0.2s;
  }
  .mu-search:focus { border-color: rgba(14,165,233,0.35); }
  .mu-search::placeholder { color: rgba(255,255,255,0.20); }
  .mu-filter-select {
    padding: 9px 14px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px; color: #E2E8F0;
    font-family: 'DM Sans', sans-serif; font-size: 13px;
    outline: none; cursor: pointer;
  }
  .mu-filter-select option { background: #1E2535; }

  /* Type badges */
  .mu-type-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 20px;
    font-size: 10px; font-weight: 700;
    font-family: 'DM Mono', monospace; letter-spacing: 0.06em;
  }
  .mu-type-admin    { background: rgba(167,139,250,0.12); color: #C4B5FD; border: 1px solid rgba(167,139,250,0.22); }
  .mu-type-fo       { background: rgba(14,165,233,0.12);  color: #38BDF8; border: 1px solid rgba(14,165,233,0.22);  }
  .mu-type-customer { background: rgba(245,158,11,0.12);  color: #FCD34D; border: 1px solid rgba(245,158,11,0.22);  }

  /* Active badge */
  .mu-active-yes { color: #86EFAC; display: flex; align-items: center; gap: 4px; font-size: 12px; }
  .mu-active-no  { color: #FCA5A5; display: flex; align-items: center; gap: 4px; font-size: 12px; }

  /* Row actions */
  .mu-row-actions {
    display: flex; align-items: center; gap: 6px;
    opacity: 0; transition: opacity 0.15s;
  }
  tr:hover .mu-row-actions { opacity: 1; }

  /* Stats strip */
  .mu-stats {
    display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap;
  }
  .mu-stat {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 16px; border-radius: 11px;
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.06);
  }
  .mu-stat-icon {
    width: 34px; height: 34px; border-radius: 9px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .mu-stat-val {
    font-size: 20px; font-weight: 800;
    color: #F1F5F9; letter-spacing: -0.03em; line-height: 1;
  }
  .mu-stat-label {
    font-size: 10px; color: rgba(255,255,255,0.28);
    font-family: 'DM Mono', monospace;
    letter-spacing: 0.10em; text-transform: uppercase;
  }

  /* Form */
  .mu-form-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  }
  .mu-form-full { grid-column: 1 / -1; }

  /* Password field */
  .mu-pw-wrap { position: relative; }
  .mu-pw-eye {
    position: absolute; right: 12px; top: 50%;
    transform: translateY(-50%);
    background: none; border: none; cursor: pointer;
    color: rgba(255,255,255,0.25); padding: 4px;
    display: flex; align-items: center;
    transition: color 0.15s;
  }
  .mu-pw-eye:hover { color: rgba(255,255,255,0.55); }

  /* Type selector */
  .mu-type-grid {
    display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px;
  }
  .mu-type-btn {
    padding: 10px 12px; border-radius: 10px;
    font-size: 11px; font-weight: 700;
    cursor: pointer; border: 1px solid transparent;
    transition: all 0.18s; text-align: center;
    display: flex; flex-direction: column;
    align-items: center; gap: 6px;
    font-family: 'DM Sans', sans-serif;
    background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.40);
  }
  .mu-type-btn:hover {
    background: rgba(255,255,255,0.07);
    color: rgba(255,255,255,0.70);
  }
  .mu-type-btn.selected-admin {
    background: rgba(167,139,250,0.12);
    color: #C4B5FD;
    border-color: rgba(167,139,250,0.30);
  }
  .mu-type-btn.selected-fo {
    background: rgba(14,165,233,0.12);
    color: #38BDF8;
    border-color: rgba(14,165,233,0.30);
  }
  .mu-type-btn.selected-customer {
    background: rgba(245,158,11,0.12);
    color: #FCD34D;
    border-color: rgba(245,158,11,0.30);
  }
  .mu-type-icon {
    width: 28px; height: 28px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
  }
`;

const TYPE_CONFIG = {
  'mideast-admin':    { label: 'Admin',    icon: <Shield size={14}/>,  iconBg: 'rgba(167,139,250,0.15)', color: '#C4B5FD', btnClass: 'selected-admin'    },
  'mideast-fo':       { label: 'Field Officer', icon: <User size={14}/>, iconBg: 'rgba(14,165,233,0.15)', color: '#38BDF8', btnClass: 'selected-fo'       },
  'mideast-customer': { label: 'Customer', icon: <Crown size={14}/>, iconBg: 'rgba(245,158,11,0.15)', color: '#FCD34D', btnClass: 'selected-customer' },
};

const EMPTY_FORM = {
  firstname: '', lastname: '', email: '',
  password: '', mobile: '', type: 'mideast-fo',
  client_id: '', is_active: true,
};

const MideastUsers = () => {
  const [users,      setUsers]      = useState([]);
  const [clients,    setClients]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [filterType, setFilterType] = useState('');
  const [showForm,   setShowForm]   = useState(false);
  const [editUser,   setEditUser]   = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [showPw,     setShowPw]     = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all users via fo/users + a dedicated all-users endpoint
      // We'll use the fo/users endpoint and supplement with auth/all
      // Since we don't have an all-users endpoint yet, we call fo/users
      // and also fetch clients for the customer link dropdown
      const [foRes, clientsRes] = await Promise.all([
        axios.get(`${BASE}/mideast/teams/fo/users`, getAuthHeader()),
        axios.get(`${BASE}/mideast/clients`,         getAuthHeader()),
      ]);

      // fo/users only returns FOs — we need all types
      // So we'll use a workaround: fetch from auth/all-users
      // which we'll add to the backend below
      // For now, get what we can
      setClients(clientsRes.data.clients || []);

      // Try the all-users endpoint
      try {
        const allRes = await axios.get(
          `${BASE}/mideast/users`,
          getAuthHeader()
        );
        setUsers(allRes.data.users || []);
      } catch {
        // Fallback to FO list if endpoint not yet added
        setUsers(foRes.data.fo_users || []);
      }
    } catch (err) {
      console.error('[Users] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Open create ────────────────────────────────────────────────
  const openCreate = () => {
    setEditUser(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowPw(false);
    setShowForm(true);
  };

  // ── Open edit ──────────────────────────────────────────────────
  const openEdit = (user, e) => {
    e.stopPropagation();
    setEditUser(user);
    setForm({
      firstname: user.firstname || '',
      lastname:  user.lastname  || '',
      email:     user.email     || '',
      password:  '',
      mobile:    user.mobile    || '',
      type:      user.type      || 'mideast-fo',
      client_id: user.client_id || '',
      is_active: user.is_active !== false,
    });
    setError('');
    setShowPw(false);
    setShowForm(true);
  };

  // ── Save ───────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.email.trim() || !form.type) {
      setError('Email and type are required');
      return;
    }
    if (!editUser && !form.password.trim()) {
      setError('Password is required for new users');
      return;
    }
    setSaving(true); setError('');

    try {
      if (editUser) {
        await axios.put(
          `${BASE}/mideast/users/${editUser.id}`,
          {
            firstname: form.firstname || null,
            lastname:  form.lastname  || null,
            mobile:    form.mobile    || null,
            type:      form.type,
            client_id: form.client_id || null,
            is_active: form.is_active,
            // Only send password if filled in
            ...(form.password.trim() ? { password: form.password } : {}),
          },
          getAuthHeader()
        );
      } else {
        await axios.post(
          `${BASE}/mideast/auth/create-user`,
          {
            firstname: form.firstname || null,
            lastname:  form.lastname  || null,
            email:     form.email,
            password:  form.password,
            mobile:    form.mobile    || null,
            type:      form.type,
            client_id: form.client_id || null,
          },
          getAuthHeader()
        );
      }
      setShowForm(false);
      fetchAll();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle active ──────────────────────────────────────────────
  const toggleActive = async (user, e) => {
    e.stopPropagation();
    try {
      await axios.put(
        `${BASE}/mideast/users/${user.id}`,
        { is_active: !user.is_active },
        getAuthHeader()
      );
      fetchAll();
    } catch (err) {
      alert('Failed to update user status');
    }
  };

  // ── Delete ─────────────────────────────────────────────────────
  const handleDelete = async (user, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${user.firstname} ${user.lastname}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BASE}/mideast/users/${user.id}`, getAuthHeader());
      fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || 'Delete failed');
    }
  };

  // ── Stats ──────────────────────────────────────────────────────
  const stats = {
    total:    users.length,
    admins:   users.filter(u => u.type === 'mideast-admin').length,
    fos:      users.filter(u => u.type === 'mideast-fo').length,
    customers:users.filter(u => u.type === 'mideast-customer').length,
  };

  // ── Filter ─────────────────────────────────────────────────────
  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch =
      u.firstname?.toLowerCase().includes(q) ||
      u.lastname?.toLowerCase().includes(q)  ||
      u.email?.toLowerCase().includes(q)     ||
      u.mobile?.toLowerCase().includes(q);
    const matchType = !filterType || u.type === filterType;
    return matchSearch && matchType;
  });

  const getInitials = (u) =>
    `${u.firstname?.[0]||''}${u.lastname?.[0]||''}`.toUpperCase() || '?';

  const getTypeClass = (type) => ({
    'mideast-admin':    'mu-type-admin',
    'mideast-fo':       'mu-type-fo',
    'mideast-customer': 'mu-type-customer',
  }[type] || 'mu-type-fo');

  return (
    <>
      <style>{STYLES}</style>

      {/* Header */}
      <div className="mu-header">
        <div>
          <div className="md-page-title">Users</div>
          <div className="md-page-sub">{users.length} Mideast user{users.length !== 1 ? 's' : ''}</div>
        </div>
        <button className="md-btn md-btn-primary" onClick={openCreate}>
          <Plus size={14}/> Add User
        </button>
      </div>

      {/* Stats */}
      <div className="mu-stats">
        {[
          { label: 'Total',     val: stats.total,     icon: <UserCog size={15}/>, bg: 'rgba(255,255,255,0.07)', color: '#E2E8F0' },
          { label: 'Admins',    val: stats.admins,    icon: <Shield size={15}/>,  bg: 'rgba(167,139,250,0.12)', color: '#C4B5FD' },
          { label: 'Field Officers', val: stats.fos,  icon: <User size={15}/>,    bg: 'rgba(14,165,233,0.12)',  color: '#38BDF8' },
          { label: 'Customers', val: stats.customers, icon: <Crown size={15}/>,   bg: 'rgba(245,158,11,0.12)', color: '#FCD34D' },
        ].map(s => (
          <div key={s.label} className="mu-stat">
            <div className="mu-stat-icon" style={{ background: s.bg, color: s.color }}>
              {s.icon}
            </div>
            <div>
              <div className="mu-stat-val">{s.val}</div>
              <div className="mu-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="mu-toolbar">
        <div className="mu-search-wrap">
          <Search size={14} className="mu-search-icon"/>
          <input
            className="mu-search"
            placeholder="Search users…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          className="mu-filter-select"
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
        >
          <option value="">All Types</option>
          <option value="mideast-admin">Admin</option>
          <option value="mideast-fo">Field Officer</option>
          <option value="mideast-customer">Customer</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="md-skeleton" style={{ height: 300, borderRadius: 14 }}/>
      ) : filtered.length === 0 ? (
        <div className="md-empty">
          <div className="md-empty-icon"><UserCog size={28}/></div>
          {search || filterType ? 'No users match your filters' : 'No users yet — add your first one'}
        </div>
      ) : (
        <div className="md-card">
          <table className="md-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Type</th>
                <th>Client</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(user => (
                <tr key={user.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 9,
                        background: TYPE_CONFIG[user.type]
                          ? `${TYPE_CONFIG[user.type].iconBg}` : 'rgba(255,255,255,0.08)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, flexShrink: 0,
                        color: TYPE_CONFIG[user.type]?.color || '#E2E8F0',
                      }}>
                        {getInitials(user)}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#E2E8F0' }}>
                          {user.firstname} {user.lastname}
                        </div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.28)', fontFamily: 'DM Mono' }}>
                          ID #{user.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.50)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Mail size={11} style={{ color: 'rgba(255,255,255,0.25)', flexShrink: 0 }}/>
                      {user.email}
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.40)', fontFamily: 'DM Mono' }}>
                    {user.mobile || '—'}
                  </td>
                  <td>
                    <span className={`mu-type-badge ${getTypeClass(user.type)}`}>
                      {TYPE_CONFIG[user.type]?.icon}
                      {TYPE_CONFIG[user.type]?.label || user.type}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.38)' }}>
                    {user.client_name || (
                      <span style={{ color: 'rgba(255,255,255,0.18)' }}>—</span>
                    )}
                  </td>
                  <td>
                    {user.is_active !== false ? (
                      <div className="mu-active-yes">
                        <CheckCircle2 size={12}/> Active
                      </div>
                    ) : (
                      <div className="mu-active-no">
                        <XCircle size={12}/> Inactive
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="mu-row-actions">
                      <button
                        className="md-btn md-btn-ghost"
                        style={{ padding: '5px 8px', fontSize: 10 }}
                        onClick={e => toggleActive(user, e)}
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {user.is_active !== false
                          ? <XCircle size={12}/>
                          : <CheckCircle2 size={12}/>
                        }
                      </button>
                      <button
                        className="md-btn md-btn-ghost"
                        style={{ padding: '5px 8px' }}
                        onClick={e => openEdit(user, e)}
                      >
                        <Edit2 size={12}/>
                      </button>
                      <button
                        className="md-btn md-btn-danger"
                        style={{ padding: '5px 8px' }}
                        onClick={e => handleDelete(user, e)}
                      >
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Modal ──────────────────────────────── */}
      {showForm && (
        <div className="md-modal-overlay">
          <div className="md-modal" style={{ maxWidth: 560 }}>
            <div className="md-modal-header">
              <UserCog size={16} color="rgba(14,165,233,0.70)" strokeWidth={1.8}/>
              <div className="md-modal-title">
                {editUser ? 'Edit User' : 'New User'}
              </div>
              <button className="md-modal-close" onClick={() => setShowForm(false)}>
                <X size={18}/>
              </button>
            </div>

            <div className="md-modal-body">
              {error && (
                <div className="md-alert md-alert-error">⚠ {error}</div>
              )}

              {/* Type selector */}
              <div className="md-field">
                <label className="md-label">User Type *</label>
                <div className="mu-type-grid">
                  {Object.entries(TYPE_CONFIG).map(([typeKey, cfg]) => (
                    <button
                      key={typeKey}
                      className={`mu-type-btn${form.type === typeKey ? ` ${cfg.btnClass}` : ''}`}
                      onClick={() => setForm(f => ({
                        ...f, type: typeKey,
                        client_id: typeKey !== 'mideast-customer' ? '' : f.client_id,
                      }))}
                    >
                      <div className="mu-type-icon" style={{
                        background: form.type === typeKey ? cfg.iconBg : 'rgba(255,255,255,0.06)',
                        color: form.type === typeKey ? cfg.color : 'rgba(255,255,255,0.30)',
                      }}>
                        {cfg.icon}
                      </div>
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mu-form-grid">
                <div className="md-field">
                  <label className="md-label">First Name</label>
                  <input
                    className="md-input"
                    placeholder="First name"
                    value={form.firstname}
                    onChange={e => setForm(f => ({ ...f, firstname: e.target.value }))}
                  />
                </div>
                <div className="md-field">
                  <label className="md-label">Last Name</label>
                  <input
                    className="md-input"
                    placeholder="Last name"
                    value={form.lastname}
                    onChange={e => setForm(f => ({ ...f, lastname: e.target.value }))}
                  />
                </div>
                <div className="md-field mu-form-full">
                  <label className="md-label">Email *</label>
                  <input
                    className="md-input"
                    type="email"
                    placeholder="user@example.com"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    disabled={!!editUser}
                    style={{ opacity: editUser ? 0.55 : 1 }}
                  />
                </div>
                <div className="md-field mu-form-full">
                  <label className="md-label">
                    {editUser ? 'New Password (leave blank to keep current)' : 'Password *'}
                  </label>
                  <div className="mu-pw-wrap">
                    <input
                      className="md-input"
                      type={showPw ? 'text' : 'password'}
                      placeholder={editUser ? 'Leave blank to keep current' : 'Min 6 characters'}
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      style={{ paddingRight: 42 }}
                    />
                    <button
                      type="button"
                      className="mu-pw-eye"
                      onClick={() => setShowPw(s => !s)}
                    >
                      {showPw ? <EyeOff size={14}/> : <Eye size={14}/>}
                    </button>
                  </div>
                </div>
                <div className="md-field">
                  <label className="md-label">Mobile</label>
                  <input
                    className="md-input"
                    placeholder="+971 50 000 0000"
                    value={form.mobile}
                    onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))}
                  />
                </div>

                {/* Client link — only for customer type */}
                {form.type === 'mideast-customer' && (
                  <div className="md-field">
                    <label className="md-label">Link to Client</label>
                    <select
                      className="md-input"
                      value={form.client_id}
                      onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                      style={{ cursor: 'pointer' }}
                    >
                      <option value="">Select client…</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.client_name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Active toggle — edit only */}
                {editUser && (
                  <div className="md-field mu-form-full">
                    <label className="md-label">Account Status</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[
                        { val: true,  label: 'Active',   icon: <CheckCircle2 size={13}/>, color: '#86EFAC', bg: 'rgba(34,197,94,0.10)',  border: 'rgba(34,197,94,0.22)'  },
                        { val: false, label: 'Inactive', icon: <XCircle size={13}/>,      color: '#FCA5A5', bg: 'rgba(239,68,68,0.10)',  border: 'rgba(239,68,68,0.22)'  },
                      ].map(opt => (
                        <button
                          key={String(opt.val)}
                          className="md-btn"
                          style={{
                            flex: 1, justifyContent: 'center',
                            background:   form.is_active === opt.val ? opt.bg    : 'rgba(255,255,255,0.04)',
                            borderColor:  form.is_active === opt.val ? opt.border: 'rgba(255,255,255,0.08)',
                            color:        form.is_active === opt.val ? opt.color : 'rgba(255,255,255,0.35)',
                            border: '1px solid',
                          }}
                          onClick={() => setForm(f => ({ ...f, is_active: opt.val }))}
                        >
                          {opt.icon} {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="md-modal-footer">
              <button
                className="md-btn md-btn-ghost"
                onClick={() => setShowForm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="md-btn md-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? <><span className="md-spinner"/> Saving…</>
                  : editUser ? 'Save Changes' : 'Create User'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MideastUsers;